import { makeAutoObservable } from 'mobx';
import { CasinoGameModelExt } from './CasinoGameModelExt';
import { FilterCategoryType } from './typesExt';
import { CasinoSearchExt } from './CasinoSearchExt';
import { CollectionClientType, GameClient } from './http-clients/adapter';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { CategoryType, CollectionType } from 'src/domains/casino/types/types';
import { RedirectState } from 'src/domains/casino/state/AppCasino/Redirect.state';
import { GameModalState } from 'src/domains/casino/state/AppCasino/game-modal/GameModal.state';

class Data {
    public readonly gameModels: AutoMap<number, CasinoGameModelExt>;
    public collectionClient: CollectionClientType;

    public constructor(
        public readonly search: CasinoSearchExt,
        public readonly gameClient: GameClient,
        private readonly collection: CollectionType,
        private readonly redirectState: RedirectState,
        private readonly gameModalState: GameModalState
    ) {
        makeAutoObservable(this);
        this.gameModels = new AutoMap(
            (gameId: number) =>
                new CasinoGameModelExt(this.collection, gameClient, gameId, this.redirectState, this.gameModalState)
        );
        this.collectionClient = this.gameClient.collectionMap.get(collection);
    }

    public get allGames(): Array<CasinoGameModelExt> {
        return this.collectionClient.categoriesAndGames.games.map((game) => this.gameModels.get(game.id));
    }

    public get allCategories(): Array<CategoryType> {
        return this.collectionClient.categoriesAndGames.categories;
    }

    public get isLoading(): boolean {
        return this.collectionClient.isLoading;
    }

    public findCategory(test: (data: CategoryType) => boolean): CategoryType | null {
        const { categories } = this.collectionClient.categoriesAndGames;

        for (const category of categories) {
            if (test(category)) {
                return category;
            }
        }

        return null;
    }
}

export class CasinoCollectionModelExt {
    private readonly data: Data;
    private readonly getGames: () => Array<CasinoGameModelExt>;

    public constructor(data: Data, getGames: () => Array<CasinoGameModelExt>) {
        makeAutoObservable(this);
        this.data = data;
        this.getGames = getGames;
    }

    public static newFromParams(
        search: CasinoSearchExt,
        collection: CollectionType,
        gameClient: GameClient,
        redirectState: RedirectState,
        gameModalState: GameModalState
    ): CasinoCollectionModelExt {
        const data = new Data(search, gameClient, collection, redirectState, gameModalState);
        return new CasinoCollectionModelExt(data, () => data.allGames);
    }

    public getGameModel(gameId: number): CasinoGameModelExt {
        return this.data.gameModels.get(gameId);
    }

    public get isSearchActive(): boolean {
        return this.data.search.isActive();
    }

    public get searchValue(): string {
        return this.data.search.searchValue;
    }

    public setSearchValue(phrase: string): void {
        this.data.search.setSearch(phrase);
    }

    public get games(): Array<CasinoGameModelExt> {
        return this.getGames();
    }

    public get categories(): Array<CategoryType> {
        return this.data.allCategories;
    }

    public get isLoading(): boolean {
        return this.data.isLoading;
    }

    public filterBySearch(): CasinoCollectionModelExt {
        return new CasinoCollectionModelExt(this.data, (): Array<CasinoGameModelExt> => {
            const gameModels = this.getGames();

            if (this.data.search.isActive()) {
                return this.data.search.filter(gameModels);
            }
            return gameModels;
        });
    }

    public filterByCategoryId(categoryId: number): CasinoCollectionModelExt {
        return new CasinoCollectionModelExt(this.data, (): Array<CasinoGameModelExt> => {
            const categoryGamesIds = this.data.findCategory((category) => category.id === categoryId)?.gameIds ?? [];
            const gameModels = this.getGames().filter((game) => categoryGamesIds.includes(game.id));
            return this.sortGamesByIdOrder(gameModels, categoryGamesIds);
        });
    }

    public filterByCategoryName(categoryName: string): CasinoCollectionModelExt {
        return new CasinoCollectionModelExt(this.data, (): Array<CasinoGameModelExt> => {
            const categoryGamesIds =
                this.data.findCategory((category) => category.name === categoryName)?.gameIds ?? [];
            const gameModels = this.getGames().filter((game) => categoryGamesIds.includes(game.id));
            return this.sortGamesByIdOrder(gameModels, categoryGamesIds);
        });
    }

    public filterByVisibleInAllGames(): CasinoCollectionModelExt {
        return new CasinoCollectionModelExt(this.data, (): Array<CasinoGameModelExt> => {
            const gameIds = this.getGameIdsFromCategories({ isVisibleInAllGames: true });
            const gameModels = this.getGames().filter((game) => gameIds.has(game.id));
            return this.sortGamesByIdOrder(gameModels, Array.from(gameIds));
        });
    }

    public filterByFavouriteGames(): CasinoCollectionModelExt {
        return new CasinoCollectionModelExt(this.data, (): Array<CasinoGameModelExt> => {
            const { favouriteGamesIds } = this.data.gameClient.customer;
            return this.getGames().filter((game) => favouriteGamesIds.includes(game.id));
        });
    }

    private getGameIdsFromCategories(filters: FilterCategoryType): Set<number> {
        const categories = this.getCategories(filters);
        const gameIds: Set<number> = new Set();

        for (const category of categories) {
            for (const gameId of category.gameIds) {
                gameIds.add(gameId);
            }
        }
        return gameIds;
    }

    private sortGamesByIdOrder(
        gameModels: Array<CasinoGameModelExt>,
        gameIds: Array<number>
    ): Array<CasinoGameModelExt> {
        const sortedGameModels: Array<CasinoGameModelExt> = [];
        for (const gameId of gameIds) {
            const gameModel = gameModels.find((game) => game.id === gameId);

            if (gameModel !== undefined) {
                sortedGameModels.push(gameModel);
            }
        }
        return sortedGameModels;
    }

    public getCategoryByName(name: string): CategoryType | null {
        return this.data.allCategories.find((category) => category.name.toLowerCase() === name.toLowerCase()) ?? null;
    }

    public getCategories(filters: FilterCategoryType): Array<CategoryType> {
        let categories = this.data.allCategories;

        if (filters.isFixed !== undefined) {
            categories = categories.filter((category) => category.fixed === filters.isFixed);
        }

        if (filters.isVisibleInNavigation !== undefined) {
            categories = categories.filter(
                (category) => category.visibleInNavigation === filters.isVisibleInNavigation
            );
        }

        if (filters.isVisibleInAllGames !== undefined) {
            categories = categories.filter((category) => category.visibleInAllGames === filters.isVisibleInAllGames);
        }

        if (filters.hasGames !== undefined) {
            if (filters.hasGames === true) {
                categories = categories.filter((category) => category.gameIds.length > 0);
            } else {
                categories = categories.filter((category) => category.gameIds.length === 0);
            }
        }

        if (filters.containsGameId !== undefined) {
            const gameId = filters.containsGameId;
            categories = categories.filter((category) => category.gameIds.includes(gameId));
        }
        return categories;
    }

    public get activeCategories(): Array<CategoryType> {
        return this.getCategories({ hasGames: true });
    }

    public get activeCategoriesVisibleInNavigation(): Array<CategoryType> {
        return this.getCategories({ isVisibleInNavigation: true });
    }

    public get fixedCategories(): Array<CategoryType> {
        return this.getCategories({ isFixed: true, hasGames: true, isVisibleInNavigation: true });
    }

    public get customCategories(): Array<CategoryType> {
        return this.getCategories({ isFixed: false });
    }

    public get customCategoriesVisibleInNavigation(): Array<CategoryType> {
        return this.getCategories({ isFixed: false, isVisibleInNavigation: true });
    }

    public get customCategoriesVisibleInAllGames(): Array<CategoryType> {
        return this.getCategories({ isFixed: false, isVisibleInAllGames: true });
    }

    public get getUserFavouriteGames(): Array<CasinoGameModelExt> {
        const { favouriteGamesIds } = this.data.gameClient.customer;
        return this.data.allGames.filter((game) => favouriteGamesIds.includes(game.id));
    }
}
